import axios from 'axios';
import API_ENDPOINTS from './endpoints';

const API_BASE_URL = 'https://api.gyansathi.com/api/v1';
// const API_BASE_URL = 'http://localhost:3000/api/v1';

// Shared across concurrent 401s so only one refresh call is ever in flight at a time.
// Without this, N simultaneous expired requests each spawn their own refresh, and
// on a rotation-based backend the second refresh arrives with an already-rotated
// (now invalid) cookie, gets 401, and enters the loop.
let refreshPromise = null;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// These endpoints must never trigger a refresh attempt. The refresh endpoint
// itself is the most critical: if it returns 401, intercepting that 401 and
// calling refresh again is the root cause of the infinite loop.
const SKIP_REFRESH_ENDPOINTS = [
  API_ENDPOINTS.AUTH.REFRESH_TOKEN,
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.LOGOUT,
  API_ENDPOINTS.AUTH.LOGOUT_ALL,
];

function shouldSkipRefresh(url = '') {
  return SKIP_REFRESH_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      try {
        // Reuse an in-progress refresh rather than starting a new one.
        // .finally() clears the lock whether the refresh succeeds or fails.
        if (!refreshPromise) {
          refreshPromise = api
            .post(API_ENDPOINTS.AUTH.REFRESH_TOKEN)
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
